import {
  InviteFullError,
  reportDomainInviteFull,
} from "@/features/workspace-invites/api/workspace-invites-api";
import { isFullDomainInvite } from "@/features/workspace-invites/domain-invite-capacity";
import { clearPendingInvite, setPendingInvite } from "@/features/workspace-invites/invite-storage";
import { supabase } from "@/lib/supabase";
import { isKairoEmail } from "../auth.utils";
import { safeRedirectPath } from "../post-auth-redirect";

/**
 * Get base app URL for auth redirects.
 * - In most cases, window.location.origin is enough (dev & prod)
 * - If VITE_APP_URL is set, it can override (useful for special setups)
 */
export function getAppUrl() {
  const envUrl = import.meta.env.VITE_APP_URL as string | undefined;

  if (envUrl && envUrl.length > 0) {
    return envUrl.replace(/\/$/, "");
  }

  // Fallback: use the current origin (works in dev & prod)
  return window.location.origin;
}

/**
 * CR-288 D2: the URL auth comes back to — the app root, or the return-to path
 * on the app's own origin. The path is re-validated here so no caller can
 * hand Supabase an off-site URL (`${origin}@evil.com` would be one).
 */
export function authRedirectUrl(redirectPath?: string): string {
  const path = safeRedirectPath(redirectPath);
  return path ? `${getAppUrl()}${path}` : getAppUrl();
}

export async function signInWithEmail(
  email: string,
  inviteCode?: string,
  redirectPath?: string,
): Promise<void> {
  // An invite that lands on its accept page overrides this below.
  let redirectTo = authRedirectUrl(redirectPath);
  clearPendingInvite();
  const inviteMetadata: {
    invite_code?: string;
    invite_type?: string;
  } = {};
  // CR-185: the link is a domain invite at its maximum.
  let fullInviteCode: string | null = null;

  // @kairo.pm addresses never need an invite code — skip the invite lookup
  // entirely so a stale/invalid invite_code in the URL can't block them.
  if (inviteCode && !isKairoEmail(email)) {
    inviteMetadata.invite_code = inviteCode;

    const { data: invite, error } = await supabase.rpc("get_invite_by_code", {
      p_invite_code: inviteCode,
    });

    if (error) {
      console.error(`Could not fetch invite by invite_code: ${error.message}`);
      throw new Error(`Failed to find a valid invite: ${error.message}`);
    }

    inviteMetadata.invite_type = invite.invite_type;

    // Only an email of the invite's domain is turned away BECAUSE the link
    // is full; another domain is refused for its own reason.
    if (isFullDomainInvite(invite) && emailDomain(email) === invite.domain?.toLowerCase()) {
      fullInviteCode = inviteCode;
    }

    setPendingInvite({
      code: inviteCode,
      type: invite.invite_type,
    });

    // CR-211: a workspace-creation invite lands on the accept page too, where
    // an existing account redeems it (a new one is answered 'already_allowed').
    if (invite.can_create_workspace || invite.invite_type != "signup") {
      redirectTo = `${getAppUrl()}/invite/${inviteCode}`;
    }
  }

  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: {
      emailRedirectTo: redirectTo,
      data: inviteMetadata,
      shouldCreateUser: true,
    },
  });

  if (error) {
    // CR-185: a full domain invite does not stop an EXISTING account (GoTrue
    // runs the invite check only when it creates a user), so the OTP request
    // is still sent. When the sign-up hook refuses it (GoTrue answers 500 to a
    // hook raise), the full link is why: say so instead of "you need a valid
    // invite", and tell the owners (best effort, re-validated by the RPC).
    // Rate limits, network, gateway and 4xx errors keep their own error.
    if (fullInviteCode && isSignupHookRefusal(error)) {
      void reportDomainInviteFull(fullInviteCode);
      throw new InviteFullError();
    }
    // Throw the original AuthError so callers get the real message and status
    throw error;
  }
}

/** GoTrue reports a `before user created` hook raise as a plain 500. */
function isSignupHookRefusal(error: { status?: number }): boolean {
  return error.status === 500;
}

function emailDomain(email: string): string {
  return email
    .slice(email.lastIndexOf("@") + 1)
    .trim()
    .toLowerCase();
}

/**
 * Sign in with OAuth provider
 */
export async function signInWithOAuth(
  provider: "github" | "google",
  redirectPath?: string,
): Promise<void> {
  const redirectTo = authRedirectUrl(redirectPath);

  const { error } = await supabase.auth.signInWithOAuth({
    provider,
    options: {
      redirectTo,
    },
  });

  if (error) {
    throw new Error(error.message);
  }
}

/**
 * Sign out current user
 */
export async function signOut(): Promise<void> {
  const { error } = await supabase.auth.signOut();

  if (error) {
    throw new Error(error.message);
  }
}
